<template>
  <main class="dauto-page" id="dauto-top">
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>

    <!-- Hero -->
    <section class="dauto-hero">
      <div class="container">
        <div class="hero-badge reveal visible">
          <span class="badge-dot"></span>
          {{ isZh ? 'ELTTEC · 数自一体化' : 'ELTTEC · IT-OT Convergence' }}
        </div>
        <h1 class="reveal reveal-delay-1 visible">
          <span class="gradient-text">{{ isZh ? 'DAuto 数字自动化产线集成' : 'DAuto Digital-Automation Integration' }}</span>
        </h1>
        <p class="hero-desc reveal reveal-delay-2 visible">
          {{ isZh ? '打通数字化管理系统与自动化产线设备，实现从管理决策到底层执行的纵向贯通，构建立体化智能工厂' : 'Bridging digital management systems with automated production equipment for end-to-end vertical integration from management decisions to field execution.' }}
        </p>
        <div class="hero-architecture reveal reveal-delay-3 visible">
          <svg viewBox="0 0 600 280" class="arch-svg">
            <rect x="100" y="20" width="400" height="50" rx="10" fill="rgba(59,130,246,.15)" stroke="rgba(59,130,246,.3)" stroke-width="1.5"/>
            <text x="300" y="52" text-anchor="middle" fill="#60a5fa" font-size="16" font-weight="600">{{ isZh ? '企业管理系统层（ERP/PLM/CRM）' : 'Enterprise Management (ERP/PLM/CRM)' }}</text>
            <line x1="300" y1="70" x2="300" y2="95" stroke="rgba(59,130,246,.4)" stroke-width="2"/>
            <polygon points="293,93 300,102 307,93" fill="rgba(59,130,246,.4)"/>

            <rect x="100" y="105" width="400" height="50" rx="10" fill="rgba(139,92,246,.15)" stroke="rgba(139,92,246,.3)" stroke-width="1.5"/>
            <text x="300" y="137" text-anchor="middle" fill="#a78bfa" font-size="16" font-weight="600">{{ isZh ? 'MOM制造运营管理平台' : 'MOM Manufacturing Operations' }}</text>
            <line x1="300" y1="155" x2="300" y2="180" stroke="rgba(139,92,246,.4)" stroke-width="2"/>
            <polygon points="293,178 300,187 307,178" fill="rgba(139,92,246,.4)"/>

            <rect x="100" y="190" width="400" height="50" rx="10" fill="rgba(16,185,129,.15)" stroke="rgba(16,185,129,.3)" stroke-width="1.5"/>
            <text x="300" y="222" text-anchor="middle" fill="#34d399" font-size="16" font-weight="600">{{ isZh ? '设备与自动化层（SCADA/PLC/CNC/IoT）' : 'Automation Layer (SCADA/PLC/CNC/IoT)' }}</text>

            <text x="510" y="52" fill="rgba(255,255,255,.3)" font-size="11">{{ isZh ? 'IT 层' : 'IT' }}</text>
            <text x="510" y="137" fill="rgba(255,255,255,.3)" font-size="11">{{ isZh ? '融合层' : 'Convergence' }}</text>
            <text x="510" y="222" fill="rgba(255,255,255,.3)" font-size="11">{{ isZh ? 'OT 层' : 'OT' }}</text>
          </svg>
        </div>
      </div>
    </section>

    <!-- MOM Section -->
    <section class="dauto-section" id="mom">
      <div class="container">
        <div class="section-header reveal">
          <span class="label">{{ isZh ? '能力一' : 'Capability 1' }}</span>
          <h2>{{ isZh ? 'MOM数字化制造运营管理' : 'MOM Digital Manufacturing Operations' }}</h2>
          <p>{{ isZh ? '以MOM为核心，构建覆盖生产、质量、设备、仓储、能源等全要素的数字化管理平台' : 'Build a comprehensive digital management platform covering production, quality, equipment, warehousing, and energy centered on MOM.' }}</p>
        </div>
        <div class="cards-grid-4">
          <div class="feature-card reveal" v-for="(card, i) in momCards" :key="i">
            <div class="card-icon" :class="'card-icon-' + (i+1)">
              <span v-html="card.icon"></span>
            </div>
            <h3>{{ card.title }}</h3>
            <p>{{ card.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Automation Section -->
    <section class="dauto-section" id="automation">
      <div class="container">
        <div class="section-header reveal">
          <span class="label">{{ isZh ? '能力二' : 'Capability 2' }}</span>
          <h2>{{ isZh ? '制造产线自动化建设与改造' : 'Production Line Automation & Upgrade' }}</h2>
          <p>{{ isZh ? '对传统产线进行自动化升级改造，集成机械臂、AGV、视觉检测等设备，实现产线智能化运行' : 'Upgrade traditional production lines with robotics, AGVs, and vision inspection for intelligent operation.' }}</p>
        </div>
        <div class="cards-grid-4">
          <div class="feature-card reveal" v-for="(card, i) in autoCards" :key="i">
            <div class="card-icon" :class="'card-icon-' + (i+1)">
              <span v-html="card.icon"></span>
            </div>
            <h3>{{ card.title }}</h3>
            <p>{{ card.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Integration Section -->
    <section class="dauto-section" id="integration">
      <div class="container">
        <div class="section-header reveal">
          <span class="label">{{ isZh ? '能力三' : 'Capability 3' }}</span>
          <h2>{{ isZh ? '数字化与自动化联动打通' : 'IT-OT Integration & Convergence' }}</h2>
          <p>{{ isZh ? '打通ERP/MOM等管理系统与底层自动化设备的数据链路，实现数-自一体、纵向贯通' : 'Connect management systems with automation devices for seamless data flow and vertical integration.' }}</p>
        </div>
        <div class="flow-steps reveal">
          <div class="flow-step" v-for="(step, i) in flowSteps" :key="i">
            <div class="step-num">{{ i + 1 }}</div>
            <div class="step-content">
              <h4>{{ step.title }}</h4>
              <p>{{ step.desc }}</p>
            </div>
            <div class="step-arrow" v-if="i < flowSteps.length - 1">&rarr;</div>
          </div>
        </div>
        <div class="cards-grid-3 reveal">
          <div class="feature-card" v-for="(card, i) in integrationCards" :key="i">
            <div class="card-icon" :class="'card-icon-' + (i+1)">
              <span v-html="card.icon"></span>
            </div>
            <h3>{{ card.title }}</h3>
            <p>{{ card.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Scenarios Section -->
    <section class="dauto-section" id="scenarios">
      <div class="container">
        <div class="section-header reveal">
          <span class="label">{{ isZh ? '应用场景' : 'Scenarios' }}</span>
          <h2>{{ isZh ? '行业应用场景' : 'Industry Scenarios' }}</h2>
          <p>{{ isZh ? 'DAuto数字自动化集成方案已成功应用于多个行业领域' : 'DAuto solutions have been successfully deployed across multiple industries.' }}</p>
        </div>
        <div class="scenario-tabs reveal">
          <button class="tab-btn" v-for="(scene, i) in scenarios" :key="i"
            :class="{ active: activeScenario === i }" @click="activeScenario = i">
            {{ scene.industry }}
          </button>
        </div>
        <div class="scenario-content reveal" v-if="scenarios[activeScenario]">
          <div class="scenario-grid">
            <div class="scenario-text">
              <h3>{{ scenarios[activeScenario].title }}</h3>
              <p>{{ scenarios[activeScenario].desc }}</p>
              <ul>
                <li v-for="(point, j) in scenarios[activeScenario].points" :key="j">{{ point }}</li>
              </ul>
            </div>
            <div class="scenario-visual">
              <div class="scenario-icon-box">{{ scenarios[activeScenario].icon }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Effects Section -->
    <section class="dauto-section" id="effects">
      <div class="container">
        <div class="section-header reveal">
          <span class="label">{{ isZh ? '实施效果' : 'Results' }}</span>
          <h2>{{ isZh ? '量化的实施效果' : 'Quantified Results' }}</h2>
        </div>
        <div class="effects-grid reveal">
          <div class="effect-card" v-for="(eff, i) in effects" :key="i">
            <span class="effect-value">{{ eff.value }}</span>
            <span class="effect-label">{{ eff.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="dauto-section" id="dauto-cta">
      <div class="container">
        <div class="cta-box reveal">
          <h2>{{ isZh ? '开启您的数字化自动化之旅' : 'Start Your Digital-Automation Journey' }}</h2>
          <p>{{ isZh ? '立即联系我们，获取专业的数自一体化解决方案咨询' : 'Contact us now for professional IT-OT integration consulting.' }}</p>
          <a href="/#contact" class="btn btn-primary cta-btn" @click.prevent="goContact">
            {{ isZh ? '立即咨询' : 'Contact Now' }}
          </a>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: 'DAutoPage',
  metaInfo() {
    const routeMeta = this.$route.meta || {}
    const isZh = this.$store.getters.isZh
    return {
      title: routeMeta.title || (isZh ? 'DAuto 数字自动化产线集成 - ELTTEC 易伦特科技' : 'DAuto Digital-Automation Integration - ELTTEC'),
      meta: [
        { vmid: 'description', name: 'description', content: routeMeta.description || (isZh ? 'ELTTEC DAuto数字自动化产线集成方案，实现设备互联与数据采集、生产流程自动化控制、IT-OT深度融合、全生命周期追溯、柔性制造管理和智能优化决策。' : 'ELTTEC DAuto digital-automation integration solution for device connectivity, process automation, IT-OT convergence, traceability, and intelligent optimization.') },
        { vmid: 'keywords', name: 'keywords', content: routeMeta.keywords || 'DAuto,数字自动化,产线集成,IT-OT融合,智能制造,工业自动化' },
        { vmid: 'og:title', property: 'og:title', content: routeMeta.title || (isZh ? 'DAuto 数字自动化产线集成 - ELTTEC 易伦特科技' : 'DAuto Digital-Automation Integration - ELTTEC') },
        { vmid: 'og:description', property: 'og:description', content: routeMeta.description || '' },
        { vmid: 'og:url', property: 'og:url', content: 'https://elttec.com/digital-automation' },
        { vmid: 'og:type', property: 'og:type', content: 'product' },
        { vmid: 'og:image', property: 'og:image', content: 'https://elttec.com/og-image-dauto.png' },
        { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { vmid: 'twitter:title', name: 'twitter:title', content: routeMeta.title || (isZh ? 'DAuto 数字自动化产线集成' : 'DAuto Digital-Automation Integration') },
        { vmid: 'twitter:description', name: 'twitter:description', content: routeMeta.description || '' },
        { vmid: 'robots', name: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large' }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: 'https://elttec.com/digital-automation' }
      ],
      script: [{
        vmid: 'schema-breadcrumb',
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'ELTTEC 易伦特科技', item: 'https://elttec.com/' },
            { '@type': 'ListItem', position: 2, name: (isZh ? 'DAuto 数字自动化产线集成' : 'DAuto Digital-Automation Integration'), item: 'https://elttec.com/digital-automation' }
          ]
        }
      }, {
        vmid: 'schema-faq',
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: (isZh ? 'DAuto数字自动化产线集成涵盖哪些核心能力？' : 'What core capabilities does DAuto cover?'),
              acceptedAnswer: {
                '@type': 'Answer',
                text: (isZh ? 'DAuto涵盖三大核心能力：MOM数字化制造运营管理（生产执行、质量追溯、设备管理和智能仓储物流）、制造产线自动化建设与改造（工业机器人集成、AGV/AMR智能物流、AI视觉检测和产线数据采集监控），以及数字化与自动化联动打通（IT-OT融合平台、IIoT工业物联网和数字孪生仿真）。' : 'DAuto covers three core capabilities: MOM digital manufacturing operations, production line automation & upgrade, and IT-OT integration & convergence with IIoT and digital twin.')
              }
            },
            {
              '@type': 'Question',
              name: (isZh ? '数字自动化集成能带来哪些量化的实施效果？' : 'What quantified results can be achieved?'),
              acceptedAnswer: {
                '@type': 'Answer',
                text: (isZh ? '典型实施效果包括：生产效率提升30-50%、运营成本降低25-40%、产品质量合格率达99.5%、设备联网率95%、产线自动化率85%、数据采集效率提升60%。' : 'Typical results: 30-50% productivity increase, 25-40% cost reduction, 99.5% quality pass rate, 95% device connectivity, 85% line automation rate, 60% data collection efficiency gain.')
              }
            },
            {
              '@type': 'Question',
              name: (isZh ? 'DAuto适用于哪些行业场景？' : 'Which industries is DAuto suitable for?'),
              acceptedAnswer: {
                '@type': 'Answer',
                text: (isZh ? 'DAuto已成功应用于汽车制造（零部件智能制造）、电子制造（电子组装与SMT产线）、机械加工（数控加工与柔性产线）、食品饮料（食品加工与包装产线）等行业领域。' : 'DAuto has been successfully deployed in automotive, electronics manufacturing, precision machining, and food & beverage industries.')
              }
            }
          ]
        }
      }]
    }
  },
  data() {
    return {
      activeScenario: 0,
      canvasCtx: null,
      animId: null
    }
  },
  computed: {
    isZh() { return this.$store.getters.isZh },
    momCards() { const z = this.isZh; return [
      { icon: '&#9881;', title: z ? '生产执行管理（MES）' : 'Production Execution (MES)', desc: z ? '实时生产调度、工单管理、作业指导与报工' : 'Real-time scheduling, work order, instruction & reporting.' },
      { icon: '&#9733;', title: z ? '质量全流程追溯（QMS）' : 'Quality Traceability (QMS)', desc: z ? '来料检验、过程控制、成品检测与SPC分析' : 'IQC, IPQC, OQC and SPC analysis.' },
      { icon: '&#9733;', title: z ? '设备全生命周期管理' : 'Equipment Lifecycle Mgmt', desc: z ? '设备台账、点检保养、OEE分析与预测维护' : 'Asset registry, inspection, OEE & predictive maintenance.' },
      { icon: '&#9889;', title: z ? '智能仓储物流（WMS）' : 'Smart Warehousing (WMS)', desc: z ? '条码/RFID出入库、库位管理、自动补货' : 'Barcode/RFID, location mgmt, auto-replenishment.' }
    ]},
    autoCards() { const z = this.isZh; return [
      { icon: '&#9881;', title: z ? '工业机器人集成' : 'Industrial Robotics', desc: z ? '六轴/SCARA/协作机器人，焊接/搬运/装配' : '6-axis, SCARA, cobots for welding, handling, assembly.' },
      { icon: '&#9881;', title: z ? 'AGV/AMR 智能物流' : 'AGV/AMR Smart Logistics', desc: z ? '自主导航、智能调度、多车协同作业' : 'Autonomous navigation, smart dispatch, multi-vehicle coordination.' },
      { icon: '&#9881;', title: z ? 'AI视觉检测系统' : 'AI Vision Inspection', desc: z ? '外观缺陷检测、尺寸测量、OCR字符识别' : 'Defect detection, dimensional measurement, OCR recognition.' },
      { icon: '&#9889;', title: z ? '产线数据采集与监控' : 'Data Acquisition & SCADA', desc: z ? 'PLC/DCS数据采集、实时监控、报警管理' : 'PLC/DCS data acquisition, real-time monitoring, alarm mgmt.' }
    ]},
    flowSteps() { const z = this.isZh; return [
      { title: z ? '设备数据采集' : 'Data Acquisition', desc: z ? 'PLC/传感器/IoT' : 'PLC/Sensors/IoT' },
      { title: z ? '边缘计算处理' : 'Edge Computing', desc: z ? '数据清洗与协议转换' : 'Data cleaning & protocol conversion' },
      { title: z ? '实时数据入湖' : 'Real-time Data Lake', desc: z ? '时序数据库存储' : 'Time-series DB storage' },
      { title: z ? 'MOM业务处理' : 'MOM Processing', desc: z ? '生产/质量/设备管理' : 'Production/Quality/EAM' },
      { title: z ? '智能分析与决策' : 'AI Analytics', desc: z ? 'OEE/SPC/预测维护' : 'OEE/SPC/Predictive' },
      { title: z ? '指令下达到设备' : 'Command Execution', desc: z ? '自动下发工单/参数' : 'Auto work order/params' },
      { title: z ? '设备自动执行' : 'Auto Execution', desc: z ? 'PLC/机器人执行' : 'PLC/Robot execution' },
      { title: z ? '结果反馈闭环' : 'Closed-loop Feedback', desc: z ? '质量数据回传' : 'Quality data feedback' }
    ]},
    integrationCards() { const z = this.isZh; return [
      { icon: '&#9881;', title: z ? '数自一体化平台' : 'IT-OT Platform', desc: z ? '统一数据中台，消除信息孤岛' : 'Unified data platform, eliminating data silos.' },
      { icon: '&#9881;', title: z ? '工业物联网（IIoT）' : 'Industrial IoT', desc: z ? '设备联网、协议适配与数据采集' : 'Device connectivity, protocol adaptation.' },
      { icon: '&#9889;', title: z ? '数字孪生与仿真' : 'Digital Twin', desc: z ? '3D虚拟工厂、工艺仿真与优化' : '3D virtual factory, process simulation & optimization.' }
    ]},
    scenarios() { const z = this.isZh; return [
      { industry: z ? '汽车制造' : 'Automotive', icon: '🚗', title: z ? '汽车零部件智能制造' : 'Auto Parts Smart Manufacturing', desc: z ? '面向汽车零部件企业，实现从原材料到成品的全流程数字化管控，集成机器人与MES系统，提升产线柔性与效率。' : 'Full digital control from raw materials to finished products, integrating robotics with MES for flexible production.', points: [z ? '产线自动化率达85%' : 'Line automation rate reaches 85%', z ? '质量追溯覆盖100%' : '100% quality traceability', z ? '设备OEE提升至82%' : 'Equipment OEE improved to 82%'] },
      { industry: z ? '电子制造' : 'Electronics', icon: '📱', title: z ? '电子组装与SMT产线' : 'Electronics Assembly & SMT', desc: z ? '为电子制造企业提供SMT生产线智能化解决方案，AI视觉检测与MES联动，实现零缺陷生产目标。' : 'Smart SMT line solutions with AI vision + MES integration for zero-defect manufacturing.', points: [z ? 'AI视觉检测替代人工' : 'AI vision replaces manual inspection', z ? '换线时间缩短60%' : 'Line changeover time reduced by 60%', z ? '产品良率提升至99.5%' : 'Yield improved to 99.5%'] },
      { industry: z ? '机械加工' : 'Machining', icon: '⚙️', title: z ? '数控加工与柔性产线' : 'CNC & Flexible Lines', desc: z ? '面向精密机械加工行业，实现CNC设备联网、自动上下料、在线检测的全自动化产线方案。' : 'CNC networking, auto loading/unloading, inline inspection for fully automated machining lines.', points: [z ? '设备联网率100%' : 'Device connectivity 100%', z ? '人均产出提升3倍' : 'Per capita output tripled', z ? '刀具寿命预测准确率90%+' : 'Tool life prediction accuracy 90%+'] },
      { industry: z ? '食品饮料' : 'Food & Beverage', icon: '🍽️', title: z ? '食品加工与包装产线' : 'Food Processing & Packaging', desc: z ? '为食品饮料行业提供符合GMP规范的自动化包装线，实现配料、灌装、包装、码垛全流程自动化。' : 'GMP-compliant automated packaging lines covering batching, filling, packaging, and palletizing.', points: [z ? '包装效率提升50%' : 'Packaging efficiency increased 50%', z ? '批次追溯精确到秒' : 'Batch traceability to seconds', z ? '人工成本降低70%' : 'Labor cost reduced 70%'] }
    ]},
    effects() { const z = this.isZh; return [
      { value: '30~50%', label: z ? '生产效率提升' : 'Productivity Increase' },
      { value: '25~40%', label: z ? '运营成本降低' : 'Operating Cost Reduction' },
      { value: '99.5%', label: z ? '产品质量合格率' : 'Quality Pass Rate' },
      { value: '95%', label: z ? '设备联网率' : 'Device Connectivity' },
      { value: '85%', label: z ? '产线自动化率' : 'Line Automation Rate' },
      { value: '60%', label: z ? '数据采集效率提升' : 'Data Collection Efficiency' }
    ]}
  },
  methods: {
    goContact() {
      this.$router.push('/').then(() => {
        this.$nextTick(() => {
          const el = document.getElementById('contact')
          if (el) el.scrollIntoView({ behavior: 'smooth' })
        })
      })
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      const particles = []
      for (let i = 0; i < 60; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 2 + 0.5,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          o: Math.random() * 0.5 + 0.1
        })
      }
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        particles.forEach(p => {
          p.x += p.vx; p.y += p.vy
          if (p.x < 0) p.x = canvas.width
          if (p.x > canvas.width) p.x = 0
          if (p.y < 0) p.y = canvas.height
          if (p.y > canvas.height) p.y = 0
          ctx.beginPath()
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(59,130,246,${p.o})`
          ctx.fill()
        })
        particles.forEach((a, i) => {
          particles.slice(i + 1).forEach(b => {
            const d = Math.hypot(a.x - b.x, a.y - b.y)
            if (d < 150) {
              ctx.beginPath()
              ctx.moveTo(a.x, a.y)
              ctx.lineTo(b.x, b.y)
              ctx.strokeStyle = `rgba(59,130,246,${0.08 * (1 - d / 150)})`
              ctx.stroke()
            }
          })
        })
        this.animId = requestAnimationFrame(animate)
      }
      animate()
      const onResize = () => {
        canvas.width = window.innerWidth
        canvas.height = window.innerHeight
      }
      window.addEventListener('resize', onResize)
      this._resizeHandler = onResize
    }
  },
  mounted() {
    this.$nextTick(() => this.initParticles())
    document.documentElement.scrollTop = 0
  },
  beforeDestroy() {
    if (this.animId) cancelAnimationFrame(this.animId)
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler)
  }
}
</script>

<style scoped>
.dauto-page { position: relative; overflow: hidden; }
.particle-canvas { position: fixed; inset: 0; z-index: 0; pointer-events: none; opacity: .6; }

.dauto-hero { position: relative; z-index: 1; text-align: center; padding: 160px 0 80px; }
.hero-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; background: rgba(34,197,94,.12); border: 1px solid rgba(34,197,94,.25); border-radius: 20px; font-size: 13px; color: #4ade80; margin-bottom: 24px; }
.badge-dot { width: 8px; height: 8px; background: #22c55e; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.dauto-hero h1 { font-size: 44px; font-weight: 800; margin-bottom: 16px; }
.hero-desc { font-size: 16px; color: #94a3b8; max-width: 680px; margin: 0 auto 40px; line-height: 1.8; }
.hero-architecture { max-width: 640px; margin: 0 auto; }
.arch-svg { width: 100%; height: auto; }

.dauto-section { position: relative; z-index: 1; padding: 80px 0; }

.cards-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.cards-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 40px; }
.feature-card {
  background: rgba(30,41,59,.8);
  border: 1px solid rgba(255,255,255,.05);
  border-radius: 14px;
  padding: 32px 24px;
  text-align: center;
  transition: all .3s;
}
.feature-card:hover { transform: translateY(-4px); border-color: rgba(34,197,94,.2); box-shadow: 0 12px 30px rgba(0,0,0,.2); }
.card-icon { width: 52px; height: 52px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; font-size: 22px; }
.card-icon-1 { background: linear-gradient(135deg, rgba(59,130,246,.2), rgba(59,130,246,.05)); color: #3b82f6; }
.card-icon-2 { background: linear-gradient(135deg, rgba(139,92,246,.2), rgba(139,92,246,.05)); color: #8b5cf6; }
.card-icon-3 { background: linear-gradient(135deg, rgba(16,185,129,.2), rgba(16,185,129,.05)); color: #10b981; }
.card-icon-4 { background: linear-gradient(135deg, rgba(245,158,11,.2), rgba(245,158,11,.05)); color: #f59e0b; }
.feature-card h3 { font-size: 15px; font-weight: 700; color: #f1f5f9; margin-bottom: 8px; }
.feature-card p { font-size: 13px; color: #94a3b8; line-height: 1.6; }

.flow-steps { display: flex; align-items: flex-start; justify-content: center; flex-wrap: wrap; gap: 12px; }
.flow-step { display: flex; align-items: center; gap: 8px; padding: 8px 4px; }
.step-num { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #22c55e, #16a34a); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; flex-shrink: 0; }
.step-content h4 { font-size: 13px; color: #f1f5f9; margin-bottom: 2px; }
.step-content p { font-size: 11px; color: #64748b; }
.step-arrow { font-size: 20px; color: rgba(34,197,94,.4); flex-shrink: 0; }

.scenario-tabs { display: flex; gap: 8px; justify-content: center; margin-bottom: 36px; flex-wrap: wrap; }
.tab-btn { padding: 10px 24px; border-radius: 8px; border: 1px solid rgba(255,255,255,.1); background: rgba(30,41,59,.5); color: #94a3b8; cursor: pointer; font-size: 14px; transition: all .3s; }
.tab-btn.active { background: rgba(34,197,94,.15); border-color: rgba(34,197,94,.3); color: #4ade80; }
.tab-btn:hover { border-color: rgba(34,197,94,.2); }
.scenario-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center; }
.scenario-text h3 { font-size: 22px; color: #f1f5f9; margin-bottom: 12px; }
.scenario-text p { color: #94a3b8; line-height: 1.7; margin-bottom: 16px; }
.scenario-text ul { list-style: none; }
.scenario-text li { color: #4ade80; font-size: 14px; padding: 6px 0; position: relative; padding-left: 20px; }
.scenario-text li::before { content: '✓'; position: absolute; left: 0; font-weight: 700; }
.scenario-visual { display: flex; align-items: center; justify-content: center; }
.scenario-icon-box { width: 160px; height: 160px; background: linear-gradient(135deg, rgba(34,197,94,.1), rgba(59,130,246,.1)); border: 1px solid rgba(34,197,94,.2); border-radius: 24px; display: flex; align-items: center; justify-content: center; font-size: 60px; }

.effects-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 16px; }
.effect-card { background: rgba(30,41,59,.8); border: 1px solid rgba(255,255,255,.05); border-radius: 14px; padding: 28px 16px; text-align: center; transition: all .3s; }
.effect-card:hover { border-color: rgba(34,197,94,.2); transform: translateY(-4px); }
.effect-value { display: block; font-size: 26px; font-weight: 800; background: linear-gradient(135deg, #4ade80, #22c55e); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 6px; }
.effect-label { font-size: 12px; color: #94a3b8; }

.cta-box { background: linear-gradient(135deg, rgba(30,41,59,.9), rgba(15,23,42,.95)); border: 1px solid rgba(34,197,94,.15); border-radius: 24px; padding: 64px 40px; text-align: center; position: relative; overflow: hidden; }
.cta-box h2 { font-size: 32px; font-weight: 700; color: #f1f5f9; margin-bottom: 12px; }
.cta-box p { font-size: 16px; color: #94a3b8; margin-bottom: 32px; }
.cta-btn { padding: 14px 40px; font-size: 16px; }

@media (max-width: 1024px) {
  .cards-grid-4 { grid-template-columns: repeat(2, 1fr); }
  .effects-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 768px) {
  .dauto-hero { padding: 120px 0 50px; }
  .dauto-hero h1 { font-size: 28px; }
  .cards-grid-4, .cards-grid-3 { grid-template-columns: 1fr; }
  .flow-steps { flex-direction: column; align-items: center; }
  .flow-step { flex-direction: column; text-align: center; }
  .step-arrow { transform: rotate(90deg); }
  .scenario-grid { grid-template-columns: 1fr; }
  .effects-grid { grid-template-columns: repeat(2, 1fr); }
  .effect-value { font-size: 22px; }
  .cta-box { padding: 40px 24px; }
  .cta-box h2 { font-size: 24px; }
}
</style>
